/**
 * useWorkshopEditForm — owns the SINGLE consolidated edit mode for the workshop
 * detail page (BON-117 / operator rework): Basics (workshop type, capacity,
 * price) and Schedule (date, start, end) edit together behind one Edit toggle
 * in the header, and one Save commits all of it as a single PATCH.
 *
 * Per-field validation populates `errors` so the offending inputs highlight.
 */
import { useState } from 'react';

import type { WorkshopDetail, WorkshopPatch } from '../../../../api/workshops';

import { parsePriceToCents, priceInputValue } from './workshopFields';

export interface WorkshopEditDraft {
  workshopTypeId: string;
  capacity: string;
  price: string;
  date: string;
  start: string;
  end: string;
}

export type WorkshopFieldErrors = Partial<Record<keyof WorkshopEditDraft, string>>;

/**
 * Seed the native date/time inputs from the venue-local wall clock
 * (`localStartDatetime` / `localEndDatetime`), NOT `startsAt`'s UTC components
 * (BON-386) — a naive `YYYY-MM-DDTHH:MM[:SS]` string, so the `YYYY-MM-DD` /
 * `HH:MM` parts the inputs expect are a direct slice (no Date, no zone). Null
 * (unpopulated local column) → blank inputs.
 */
function localParts(local: string | null): { date: string; time: string } {
  if (!local) return { date: '', time: '' };
  return { date: local.slice(0, 10), time: local.slice(11, 16) };
}

/**
 * Recombine the operator's date + time inputs into a `Z`-tagged ISO whose
 * components ARE the venue-local wall clock. UNCHANGED submit encoding
 * (BON-386): the server now interprets this as venue-local and derives the
 * absolute instant, so the tag is a carrier for the wall clock, not a claim of
 * UTC.
 */
function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}:00.000Z`).toISOString();
}

function seedDraft(workshop: WorkshopDetail): WorkshopEditDraft {
  const start = localParts(workshop.localStartDatetime);
  const end = localParts(workshop.localEndDatetime);
  return {
    workshopTypeId: workshop.workshopTypeId,
    capacity: String(workshop.capacity),
    price: priceInputValue(workshop.priceCents),
    date: start.date,
    start: start.time,
    end: end.time,
  };
}

/**
 * Date/start/end checks, in their own function so `validate` stays under the
 * complexity cap.
 *
 * Presence is checked BEFORE `toIso` is allowed near a value: a blank date or
 * time builds a malformed ISO string and `new Date(...).toISOString()` THROWS on
 * it. `validate` runs outside `submit`'s try/catch, so that throw surfaces as a
 * dead Save button with no message rather than a field error — and blank inputs
 * need no operator action to reach, since a workshop whose stored local columns
 * are null seeds them blank (BON-386).
 */
function scheduleErrors(draft: WorkshopEditDraft): WorkshopFieldErrors {
  if (!draft.date || !draft.start || !draft.end) {
    return {
      ...(draft.date ? {} : { date: 'Date is required.' }),
      ...(draft.start ? {} : { start: 'Start time is required.' }),
      ...(draft.end ? {} : { end: 'End time is required.' }),
    };
  }
  const startsAt = new Date(toIso(draft.date, draft.start)).getTime();
  const endsAt = new Date(toIso(draft.date, draft.end)).getTime();
  return endsAt <= startsAt ? { end: 'End must be after start.' } : {};
}

/** Validate the draft; returns a field→message map (empty when valid). */
function validate(draft: WorkshopEditDraft): WorkshopFieldErrors {
  const errors: WorkshopFieldErrors = {};
  const capacity = Number(draft.capacity);
  if (!Number.isInteger(capacity) || capacity < 0) {
    errors.capacity = 'Capacity must be a whole number.';
  }
  if (parsePriceToCents(draft.price) === null) {
    errors.price = 'Price must be a valid amount.';
  }
  return { ...errors, ...scheduleErrors(draft) };
}

/** Build the PATCH from a validated draft. */
function buildPatch(draft: WorkshopEditDraft, workshop: WorkshopDetail): WorkshopPatch {
  const patch: WorkshopPatch = {
    capacity: Number(draft.capacity),
    priceCents: parsePriceToCents(draft.price) as number,
  };
  if (draft.workshopTypeId !== workshop.workshopTypeId) {
    patch.workshopTypeId = draft.workshopTypeId;
  }
  patch.startsAt = toIso(draft.date, draft.start);
  patch.endsAt = toIso(draft.date, draft.end);
  return patch;
}

export interface WorkshopEditForm {
  editing: boolean;
  draft: WorkshopEditDraft;
  errors: WorkshopFieldErrors;
  saving: boolean;
  saveError: string | null;
  setField: (key: keyof WorkshopEditDraft, value: string) => void;
  startEditing: () => void;
  cancel: () => void;
  submit: () => Promise<void>;
}

/**
 * @param workshop  the loaded record (source of truth + reseed key)
 * @param save      commits one PATCH (page wires this to the update mutation)
 */
export function useWorkshopEditForm(
  workshop: WorkshopDetail,
  save: (patch: WorkshopPatch) => Promise<void>,
): WorkshopEditForm {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WorkshopEditDraft>(() => seedDraft(workshop));
  const [errors, setErrors] = useState<WorkshopFieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reseed from the server value whenever it changes (render-phase reset, no
  // effect loop), e.g. after a save writes the fresh workshop into the cache.
  const [appliedKey, setAppliedKey] = useState(workshop.updatedAt);
  if (appliedKey !== workshop.updatedAt) {
    setAppliedKey(workshop.updatedAt);
    setDraft(seedDraft(workshop));
  }

  const setField = (key: keyof WorkshopEditDraft, value: string) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const startEditing = () => {
    setSaveError(null);
    setErrors({});
    setDraft(seedDraft(workshop));
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setErrors({});
    setSaveError(null);
    setDraft(seedDraft(workshop));
  };

  const submit = async () => {
    const found = validate(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      await save(buildPatch(draft, workshop));
      setEditing(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  return {
    editing,
    draft,
    errors,
    saving,
    saveError,
    setField,
    startEditing,
    cancel,
    submit,
  };
}
